/**
 * ImageCropper — Wraps Cropper.js for interactive background-image
 * region selection inside a modal overlay.
 *
 * Returns normalised (0-1) crop coordinates that feed directly into
 * {@link BackgroundProcessor}.
 *
 * @module ImageCropper
 */

import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

/**
 * @typedef {Object} NormalisedCropRegion
 * @property {number} x      Left   (0-1)
 * @property {number} y      Top    (0-1)
 * @property {number} width  Width  (0-1)
 * @property {number} height Height (0-1)
 */

/**
 * Interactive image cropper that renders inside a modal overlay.
 *
 * Usage:
 * ```js
 * const cropper = new ImageCropper(document.getElementById('app'));
 * await cropper.loadImage(file);
 * cropper.setAspectRatio(1600, 300);
 * // …user drags crop box…
 * const region = cropper.getCropRegion();
 * cropper.destroy();
 * ```
 */
export class ImageCropper {
  /** @type {HTMLElement} */
  #container;

  /** @type {HTMLDivElement | null} */
  #modal = null;

  /** @type {HTMLImageElement | null} */
  #imageEl = null;

  /** @type {Cropper | null} */
  #cropper = null;

  /** @type {number | null} */
  #aspectRatio = null;

  /** @type {((region: NormalisedCropRegion | null) => void) | null} */
  #resolvePromise = null;

  /**
   * @param {HTMLElement} containerElement  Element to append the modal into
   */
  constructor(containerElement) {
    this.#container = containerElement;
    this.#injectStyles();
  }

  /**
   * Load an image into the cropper and show the modal.
   *
   * @param {string | File | Blob} imageSource
   *   A URL / data-URL string, or a File / Blob to load.
   * @returns {Promise<NormalisedCropRegion | null>}
   *   Resolves with the crop region when the user confirms, or null if cancelled.
   */
  async loadImage(imageSource) {
    // Tear down any prior instance
    this.#destroyCropper();

    const src = await this.#resolveImageSrc(imageSource);

    return new Promise((resolve) => {
      this.#resolvePromise = resolve;
      this.#buildModal(src);
    });
  }

  /**
   * Constrain the crop box to match specific banner dimensions.
   *
   * @param {number} width   Banner width
   * @param {number} height  Banner height
   */
  setAspectRatio(width, height) {
    this.#aspectRatio = width / height;
    if (this.#cropper) {
      this.#cropper.setAspectRatio(this.#aspectRatio);
    }
  }

  /**
   * Read the current crop region as normalised 0-1 coordinates.
   *
   * @returns {NormalisedCropRegion}
   */
  getCropRegion() {
    if (!this.#cropper || !this.#imageEl) {
      throw new Error('No image loaded in cropper');
    }

    const data = this.#cropper.getData(true); // rounded px values
    const imgData = this.#cropper.getImageData();

    return {
      x: data.x / imgData.naturalWidth,
      y: data.y / imgData.naturalHeight,
      width: data.width / imgData.naturalWidth,
      height: data.height / imgData.naturalHeight,
    };
  }

  /**
   * Get a canvas of the cropped region at a specific size.
   *
   * @param {number} width   Desired output width
   * @param {number} height  Desired output height
   * @returns {HTMLCanvasElement}
   */
  getCroppedCanvas(width, height) {
    if (!this.#cropper) {
      throw new Error('No image loaded in cropper');
    }

    return this.#cropper.getCroppedCanvas({ width, height });
  }

  /**
   * Close the modal and release Cropper.js resources.
   */
  destroy() {
    this.#destroyCropper();
    this.#removeModal();
  }

  // ── Private: Modal UI ───────────────────────────────────────────────

  /**
   * Build and show the modal overlay containing the cropper.
   * @param {string} src  Image source URL
   * @private
   */
  #buildModal(src) {
    // Overlay
    this.#modal = document.createElement('div');
    this.#modal.className = 'bac-cropper-overlay';

    // Dialog container
    const dialog = document.createElement('div');
    dialog.className = 'bac-cropper-dialog';

    // Header
    const header = document.createElement('div');
    header.className = 'bac-cropper-header';
    header.innerHTML = `
      <h3>Crop Background Image</h3>
      <span class="bac-cropper-hint">Drag to select the region that will be used as the banner background</span>
    `;

    // Image wrapper (Cropper.js needs a block-level container)
    const imgWrapper = document.createElement('div');
    imgWrapper.className = 'bac-cropper-img-wrapper';

    this.#imageEl = document.createElement('img');
    this.#imageEl.src = src;
    this.#imageEl.style.maxWidth = '100%';
    this.#imageEl.style.display = 'block';
    imgWrapper.appendChild(this.#imageEl);

    // Action buttons
    const actions = document.createElement('div');
    actions.className = 'bac-cropper-actions';

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.className = 'bac-cropper-btn bac-cropper-btn--cancel';
    cancelBtn.addEventListener('click', () => this.#handleCancel());

    const confirmBtn = document.createElement('button');
    confirmBtn.textContent = 'Apply Crop';
    confirmBtn.className = 'bac-cropper-btn bac-cropper-btn--confirm';
    confirmBtn.addEventListener('click', () => this.#handleConfirm());

    actions.appendChild(cancelBtn);
    actions.appendChild(confirmBtn);

    dialog.appendChild(header);
    dialog.appendChild(imgWrapper);
    dialog.appendChild(actions);
    this.#modal.appendChild(dialog);
    this.#container.appendChild(this.#modal);

    // Close on backdrop click
    this.#modal.addEventListener('click', (e) => {
      if (e.target === this.#modal) this.#handleCancel();
    });

    // Escape key
    this._escHandler = (e) => {
      if (e.key === 'Escape') this.#handleCancel();
    };
    document.addEventListener('keydown', this._escHandler);

    // Initialise Cropper.js once the image has loaded
    this.#imageEl.addEventListener('load', () => {
      this.#cropper = new Cropper(this.#imageEl, {
        viewMode: 1,
        guides: true,
        responsive: true,
        autoCropArea: 0.8,
        aspectRatio: this.#aspectRatio ?? NaN,
        background: true,
        movable: true,
        zoomable: true,
        cropBoxResizable: true,
        cropBoxMovable: true,
      });
    }, { once: true });
  }

  /** @private */
  #handleConfirm() {
    const region = this.getCropRegion();
    this.#cleanup();
    if (this.#resolvePromise) {
      this.#resolvePromise(region);
      this.#resolvePromise = null;
    }
  }

  /** @private */
  #handleCancel() {
    this.#cleanup();
    if (this.#resolvePromise) {
      this.#resolvePromise(null);
      this.#resolvePromise = null;
    }
  }

  /** @private */
  #cleanup() {
    this.#destroyCropper();
    this.#removeModal();
    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler);
      this._escHandler = null;
    }
  }

  /** @private */
  #destroyCropper() {
    if (this.#cropper) {
      this.#cropper.destroy();
      this.#cropper = null;
    }
    this.#imageEl = null;
  }

  /** @private */
  #removeModal() {
    if (this.#modal) {
      this.#modal.remove();
      this.#modal = null;
    }
  }

  // ── Private: Helpers ────────────────────────────────────────────────

  /**
   * Resolve various image-source types to a usable src string.
   * @param {string | File | Blob} source
   * @returns {Promise<string>}
   * @private
   */
  async #resolveImageSrc(source) {
    if (typeof source === 'string') return source;

    if (source instanceof Blob || source instanceof File) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(/** @type {string} */ (reader.result));
        reader.onerror = () => reject(new Error('Failed to read image file'));
        reader.readAsDataURL(source);
      });
    }

    throw new Error('imageSource must be a URL string, File, or Blob');
  }

  /**
   * Inject scoped CSS for the cropper modal. Runs once per page.
   * @private
   */
  #injectStyles() {
    if (document.getElementById('bac-cropper-styles')) return;

    const style = document.createElement('style');
    style.id = 'bac-cropper-styles';
    style.textContent = /* css */ `
      .bac-cropper-overlay {
        position: fixed;
        inset: 0;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.75);
        backdrop-filter: blur(4px);
        animation: bac-cropper-fadeIn 0.2s ease;
      }

      @keyframes bac-cropper-fadeIn {
        from { opacity: 0; }
        to   { opacity: 1; }
      }

      .bac-cropper-dialog {
        background: #1e1e1e;
        border: 1px solid #333;
        border-radius: 12px;
        width: 90vw;
        max-width: 960px;
        max-height: 90vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
      }

      .bac-cropper-header {
        padding: 16px 20px;
        border-bottom: 1px solid #333;
      }

      .bac-cropper-header h3 {
        margin: 0 0 4px;
        font-size: 16px;
        font-weight: 600;
        color: #f0f0f0;
      }

      .bac-cropper-hint {
        font-size: 13px;
        color: #888;
      }

      .bac-cropper-img-wrapper {
        flex: 1;
        min-height: 0;
        overflow: hidden;
        background: #111;
        max-height: 60vh;
      }

      .bac-cropper-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 14px 20px;
        border-top: 1px solid #333;
      }

      .bac-cropper-btn {
        padding: 8px 20px;
        border: none;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.15s, transform 0.1s;
      }

      .bac-cropper-btn:active {
        transform: scale(0.97);
      }

      .bac-cropper-btn--cancel {
        background: #333;
        color: #ccc;
      }

      .bac-cropper-btn--cancel:hover {
        background: #444;
      }

      .bac-cropper-btn--confirm {
        background: #1d76ba;
        color: #fff;
      }

      .bac-cropper-btn--confirm:hover {
        background: #428bca;
      }
    `;
    document.head.appendChild(style);
  }
}
